import { getConvenienceVariables } from '../convenienceVariables';
import { conditions as globalConditions } from './global';
/*
  The commented out text above each variable
  should match the condition's name in Kentico
  and the variable key should match its codename
*/
export const hospitalIndemnityConditions = {
    // User's first time visiting the HI module
    hi_condition_is_new_user: (data) => {
        return data.user.Global.SeenOpening.hospitalIndemnity !== true;
    },
    // User has selected HI coverage
    hi_condition_has_selected_hi_covg: (data) => {
        return data.user.HI.selectionStatus === 'Selected';
    },
    // User has selected or waived HI coverage
    hi_condition_has_selected_or_waived_hi_covg: (data) => {
        return (data.user.HI.selectionStatus === 'Selected' ||
            data.user.HI.selectionStatus === 'Waived');
    },
    // User has not made a decision about HI coverage yet
    hi_condition_has_not_made_hi_covg_decision: (data) => {
        return (data.user.HI.selectionStatus !== 'Selected' &&
            data.user.HI.selectionStatus !== 'Waived');
    },
    // User said in Medical Outro that they're interested in enrolling in HI
    hi_condition_was_interested_in_hi_covg_in_med_outro: (data) => {
        /*
        We basically infer that if the user hasn't seen HI opening content but wantsCoverage is true,
        that must mean they said they were intersted in HI coverage when asked in Med Outro rather than within the HI flow.
      */
        return (data.user.Global.SeenOpening.hospitalIndemnity !== true &&
            data.user.HI.wantsCoverage === true);
    },
    // User said in Medical Outro that they're not interested in enrolling in HI
    hi_condition_was_not_interested_in_hi_covg_in_med_outro: (data) => {
        /*
        We basically infer that if the user hasn't seen HI opening content but wantsCoverage is false (rather than undefined),
        that must mean they said they weren't intersted in HI coverage when asked in Med Outro rather than within the HI flow.
      */
        return (data.user.Global.SeenOpening.hospitalIndemnity !== true &&
            data.user.HI.wantsCoverage === false);
    },
    // User hasn't seen the plan explanation content
    hi_condition_hasnt_seen_plan_exp: (data) => {
        return data.user.HI.seenPlanExp !== true;
    },
    // User has seen the plan explanation content
    hi_condition_has_seen_plan_exp: (data) => {
        return data.user.HI.seenPlanExp === true;
    },
    // Plan has admissions or confinement or ICU
    hi_condition_plan_has_admissions_or_confinements_or_icu_covg: (data) => {
        return Boolean(data.plan.hasHospitalAdmission ||
            data.plan.hasHospitalConfinement ||
            data.plan.hasIcu);
    },
    // Plan has other covered services
    hi_condition_plan_has_other_covered_services: (data) => {
        return Boolean(data.plan.hasCoveredServices);
    },
    // User has eligible dependents
    hi_condition_has_eligible_dependents: (data) => {
        const convVars = getConvenienceVariables(data);
        return Boolean(convVars.HI_eligibleKidsNum > 0 || convVars.HI_hasEligibleSignificantOther);
    },
    // User has eligible significant other AND eligible kid(s)
    hi_condition_has_eligible_so_and_eligible_kids: (data) => {
        const convVars = getConvenienceVariables(data);
        return Boolean(convVars.HI_eligibleKidsNum > 0 && convVars.HI_hasEligibleSignificantOther);
    },
    // User has eligible significant other
    hi_condition_has_eligible_so: (data) => {
        const convVars = getConvenienceVariables(data);
        return convVars.HI_hasEligibleSignificantOther;
    },
    // User has eligible kid(s)
    hi_condition_has_eligible_kids: (data) => {
        const convVars = getConvenienceVariables(data);
        return convVars.HI_eligibleKidsNum > 0;
    },
    // Plan has lower payouts for at least one eligible family member
    hi_condition_plan_has_lower_payouts_for_any_eligible_family: (data) => {
        const convVars = getConvenienceVariables(data);
        return Boolean((!data.plan.spouseHasSameBenefits &&
            convVars.HI_hasEligibleSignificantOther) ||
            (!data.plan.childHasSameBenefits && convVars.HI_eligibleKidsNum > 0));
    },
    // Plan has admissions AND confinements AND ICU
    hi_condition_plan_has_admissions_confinements_icu_covg: (data) => {
        return Boolean(data.plan.hasHospitalAdmission &&
            data.plan.hasHospitalConfinement &&
            data.plan.hasIcu);
    },
    // Plan has admissions AND confinements, does NOT have ICU
    hi_condition_plan_has_admissions_confinements_covg_no_icu: (data) => {
        return Boolean(data.plan.hasHospitalAdmission &&
            data.plan.hasHospitalConfinement &&
            !data.plan.hasIcu);
    },
    // Plan has admissions AND ICU, does NOT have confinements
    hi_condition_plan_has_admissions_icu_covg_no_confinements: (data) => {
        return Boolean(data.plan.hasHospitalAdmission &&
            !data.plan.hasHospitalConfinement &&
            data.plan.hasIcu);
    },
    // Plan has confinements AND ICU, does NOT have admissions
    hi_condition_plan_has_confinments_icu_covg_no_admissions: (data) => {
        return Boolean(!data.plan.hasHospitalAdmission &&
            data.plan.hasHospitalConfinement &&
            data.plan.hasIcu);
    },
    // Plan has admissions, does NOT have confinements, does NOT have ICU
    hi_condition_plan_has_admissions_covg_only: (data) => {
        return Boolean(data.plan.hasHospitalAdmission &&
            !data.plan.hasHospitalConfinement &&
            !data.plan.hasIcu);
    },
    // Plan has confinements, does NOT have admissions, does NOT have ICU
    hi_condition_plan_has_confinements_covg_only: (data) => {
        return Boolean(!data.plan.hasHospitalAdmission &&
            data.plan.hasHospitalConfinement &&
            !data.plan.hasIcu);
    },
    // Plan has admissions
    hi_condition_plan_has_admissions: (data) => {
        return Boolean(data.plan.hasHospitalAdmission);
    },
    // Plan has confinements
    hi_condition_plan_has_confinements: (data) => {
        return Boolean(data.plan.hasHospitalConfinement);
    },
    // Plan has ICU
    hi_condition_plan_has_icu: (data) => {
        return Boolean(data.plan.hasIcu);
    },
    // plan has pregnancy coverage
    hi_condition_plan_has_pregnancy_covg: (data) => {
        return Boolean(data.plan.hasPregnancy);
    },
    // plan has guaranteed issue (pre-existing conditions do not affect plan coverage)
    hi_condition_plan_has_guaranteed_issue: (data) => {
        return Boolean(data.plan.hasGuaranteedIssue);
    },
    // plan does not have guaranteed issue (pre-existing conditions do affect plan coverage)
    hi_condition_plan_does_not_have_guaranteed_issue: (data) => {
        return Boolean(!data.plan.hasGuaranteedIssue);
    },
    // User has exactly two eligible children
    hi_condition_has_exactly_two_eligible_kids: (data) => {
        const convVars = getConvenienceVariables(data);
        return convVars.HI_eligibleKidsNum === 2;
    },
    // User has 3 or more eligible children
    hi_condition_has_three_or_more_eligible_kids: (data) => {
        const convVars = getConvenienceVariables(data);
        return convVars.HI_eligibleKidsNum >= 3;
    },
    // Has eligible dependents, waived HI or hasn't made a selection yet
    hi_condition_eligible_dependents_waived_or_no_selection: (data) => {
        const convVars = getConvenienceVariables(data);
        return (data.user.HI.selectionStatus !== 'Selected' &&
            (convVars.HI_hasEligibleSignificantOther ||
                convVars.HI_eligibleKidsNum > 0));
    },
    // User has not completed all available modules yet
    hi_condition_has_rec_module: globalConditions.global_condition_has_rec_module,
    // Plan has a pregnancy text note
    hi_condition_plan_has_pregnancy_note: (data) => {
        return Boolean(data.plan.hasPregnancyNote);
    },
};
//# sourceMappingURL=hospitalIndemnity.js.map